<template>
    <el-dialog
        :title="$t('Bind Referrer')"
        :visible.sync="visible"
        width="70%"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="!isBindingPending"
    >
        <div v-if="isBindingPending">
            <el-form>
                <el-form-item :label="$t('Referrer Address')">
                    <el-input v-model="formatAddress" disabled>
                        <template slot="append">
                            <el-button type="text" @click="copyAddress">
                                <i class="el-icon-document"></i>
                            </el-button>
                        </template>
                    </el-input>
                </el-form-item>
            </el-form>
        </div>

        <div v-else-if="isBindingCompleted" class="binding-completed">
            <i class="el-icon-success"></i>
            <p>{{ $t('Binding completed successfully') }}</p>
        </div>

        <div v-else-if="isBindingError" class="binding-error">
            <i class="el-icon-error"></i>
            <p>{{ $t('Binding failed') }}</p>
            <el-button type="primary" @click="retryBinding">
                {{ $t('Retry') }}
            </el-button>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button 
                v-if="isBindingPending"
                type="primary" 
                @click="submit" 
                :loading="loading"
            >
                {{ $t('Confirm') }}
            </el-button>
            <el-button 
                v-else-if="isBindingCompleted"
                @click="$emit('close')"
            >
                {{ $t('Close') }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import i18n from '../../../locales/index'
import { getMobileSaleContract, getSaleStorageContract } from "@/utils/contract";
import web3 from "@/utils/myweb3";
import { copyToClipboard } from "@/utils";
import { mapState } from "vuex";

export default {
    computed: {
        ...mapState(['userAdd']),
        isBindingPending() {
            return this.bindingStatus === 'pending';
        },
        isBindingCompleted() {
            return this.bindingStatus === 'completed';
        },
        isBindingError() {
            return this.bindingStatus === 'error';
        },
        formatAddress() {
            return this.referrerAddress.slice(0, 10) + '  .  .  .  ' + this.referrerAddress.slice(-6);
        }
    },
    data() {
        return {
            visible: true,
            loading: false,
            bindingStatus: 'pending',
            referrerAddress: window.refer || '', // 直接从 window.refer 获取
        }
    },
    methods: {
        copyAddress() {
            copyToClipboard(this.referrerAddress);
            this.$message.success(this.$t('Copied to clipboard'));
        },
        async bindReferral() {
            try {
                this.loading = true;
                const contract = getMobileSaleContract();
                
                // 检查是否可以绑定
                const canBind = await contract.methods.canBindReferral(this.userAdd, this.referrerAddress).call();
                if (!canBind) {
                    throw new Error(this.$t('Cannot bind referral'));
                }
                // 执行绑定
                const tx = await contract.methods.bindReferral(this.referrerAddress)
                    .send({ from: this.userAdd });
                console.log(tx, "bind referrer tx");
                // 监听绑定完成事件
                contract.events.ReferralBindingCompleted({
                    filter: { 
                        user: this.userAdd,
                        referrer: this.referrerAddress
                    },
                    fromBlock: tx.blockNumber
                }, async (error, event) => {
                    if (error) {
                        console.error('Referral binding event error:', error);
                        this.handleError(error);
                        return;
                    }

                    try {
                        // 处理绑定完成事件
                        console.log('Referral binding completed:', event);
                        
                        // 更新本地状态
                        this.bindingStatus = 'completed';
                        
                        // 更新 store 中的推荐人信息
                        await this.$store.dispatch('updateReferrer', {
                            user: event.returnValues.user,
                            referrer: event.returnValues.referrer,
                            productId: event.returnValues.productId
                        });

                        // 显示成功消息
                        this.$message.success(this.$t('Referral binding successful'));
                        
                        // 关闭对话框
                        this.$emit('close');
                        
                        // 触发父组件的刷新
                        this.$emit('binding-completed', {
                            user: event.returnValues.user,
                            referrer: event.returnValues.referrer,
                            productId: event.returnValues.productId
                        });

                    } catch (error) {
                        console.error('Error handling binding completion:', error);
                        this.handleError(error);
                    }
                });

                return tx;
            } catch (error) {
                console.error('Error binding referral:', error);
                this.handleError(error);
            } finally {
                this.loading = false;
            }
        },

        handleError(error) {
            this.loading = false;
            this.bindingStatus = 'error';
            this.$message.error(error.message || this.$t('Binding failed'));
        },

        async retryBinding() {
            if (this.bindingStatus === 'error') {
                this.bindingStatus = 'pending';
                await this.submit();
            }
        },

        async submit() {
            if (!web3.utils.isAddress(this.referrerAddress)) {
                this.$message.error(i18n.t('views_home_components_bind_dialog_41'));
                return;
            }

            // 检查用户是否已购买
            const storageContract = getSaleStorageContract();
            const userData = await storageContract.methods.getUserData(this.userAdd).call();
            
            if (userData.purchasedProductId) {
                this.$message.error(this.$t('Cannot bind after purchase'));
                return;
            }

            let loading = this.$loading();
            try {
                const result = await this.bindReferral();

                console.log('Binding result:', result);
                this.visible = false;
                this.$emit("success");
                this.$message.success(i18n.t('views_home_components_bind_dialog_42'));
            } catch (error) {
                console.error('Binding error:', error);
                this.handleError(error);
            } finally {
                loading.close();
            }
        }
    }
}
</script>

<style scoped>
.binding-completed,
.binding-error {
    text-align: center;
    padding: 20px;
}

.binding-completed i {
    color: #67C23A;
    font-size: 48px;
}

.binding-error i {
    color: #F56C6C;
    font-size: 48px;
}

.binding-error .el-button {
    margin-top: 20px;
}
</style>